// utils/booking/metadata.ts
//
// The runtime page's copy of the custom-flow contract: the `meta` / `intake` /
// `notes` query params booking.js writes into the iframe URL, and the
// "Powered by Workspace369" credit policy for the in-frame footer.
//
// The block between the two SHARED SANITISER markers is DUPLICATED VERBATIM
// from embed/src/bookingHost.ts (house convention: booking.js may import only
// ./ files, so shared code is duplicated, not packaged across the
// embed/runtime build boundary — see components/booking/embedBridge.ts) —
// KEEP IN SYNC. utils/booking/metadata.parity.test.ts reads both files and
// fails when the two blocks drift.

/**
 * `rel` for the in-frame "Powered by Workspace369" credit — exactly `noopener`:
 * no `nofollow`, no `sponsored`, no `noreferrer`. Mirrors POWERED_BY_REL in
 * embed/src/bookingHost.ts; the one line to change if the policy flips.
 */
export const POWERED_BY_REL = 'noopener'

/**
 * The client-facing page a credit sits on; it becomes the link's `utm_medium`. Only the first five (plus
 * `contact`) look up the owner's affiliate code (server/api/powered-by.get.ts); the rest carry the UTM link only.
 */
export type PoweredBySurface =
  | 'invoice' | 'proposal' | 'contract' | 'booking' | 'portal'
  | 'contact' | 'funnel' | 'share' | 'change-order' | 'note' | 'public-page' | 'statement' | 'pay' | 'legal'

/** Existing Rewardful affiliate link tokens use `via`; visitor referral UUIDs are different. */
export function affiliateTokenFromUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value)
    if (url.protocol !== 'https:' || !['workspace369.com', 'www.workspace369.com', 'app.workspace369.com'].includes(url.hostname)) return undefined
    const token = url.searchParams.get('via')
    return token && /^[a-zA-Z0-9_-]{1,128}$/.test(token) ? token : undefined
  }
  catch { return undefined }
}

/** Shared destination for every client-facing credit; never carries a document token or client data. */
export function poweredByPageHref(placement: 'embed' | 'page', surface: PoweredBySurface = 'booking', affiliateToken?: string): string {
  const url = new URL('https://workspace369.com/')
  url.searchParams.set('utm_source', 'powered_by')
  url.searchParams.set('utm_medium', surface)
  url.searchParams.set('utm_campaign', 'powered-by')
  url.searchParams.set('utm_content', placement)
  if (affiliateToken && /^[a-zA-Z0-9_-]{1,128}$/.test(affiliateToken)) url.searchParams.set('via', affiliateToken)
  return url.href
}

// ── BEGIN SHARED SANITISER ────────────────────────────────────────────────
// Contract (plan 2026-09-07-booking-custom-flow-tier1, "Shared contract"),
// mirrored RULE FOR RULE from the trusted API side — Workspace369-API
// functions-schedules/src/workspace/schedules/publicBooking/bookingMetadata.ts
// (its header numbers the nine rules; keep the two in sync):
//   metadata: a plain object; ≤ 20 keys counted on the INPUT (21+ drops the
//   WHOLE map); a key matches /^[A-Za-z0-9_.-]{1,40}$/ AND is not a
//   Firestore-reserved dunder /^__.*__$/ (a bad key is dropped, the rest
//   kept); a string value has CR/LF runs → ONE space, is trimmed, capped at
//   500 UTF-16 units, loses a trailing lone high surrogate the cap split off
//   an emoji, is trimmed again, and an EMPTY result is dropped; a number is
//   kept only when finite; a boolean as-is; anything else dropped; the
//   serialised JSON of the sanitised map must be ≤ 4096 bytes else the WHOLE
//   map is dropped; own-property reads only (Object.keys), so a JSON
//   `__proto__` key can neither pollute nor masquerade as present.
//   intake: keyed by question id (same dunder rejection) → string | string[]
//   | boolean, ≤ 4096 bytes serialised.  notes: ≤ NOTES_MAX chars.

export type BookingMetadata = Record<string, string | number | boolean>
export type BookingIntakeAnswers = Record<string, string | string[] | boolean>

export const NOTES_MAX = 2000
const METADATA_MAX_KEYS = 20
const METADATA_KEY_RE = /^[A-Za-z0-9_.-]{1,40}$/
/** Firestore-reserved field names (`__name__`, `__type__`, …) — and `__proto__`: never storable, never assigned. */
const RESERVED_KEY_RE = /^__.*__$/
const METADATA_VALUE_MAX = 500
const METADATA_MAX_BYTES = 4096
const INTAKE_ID_RE = /^[A-Za-z0-9_-]{1,128}$/
const INTAKE_ANSWER_MAX = 2000
const INTAKE_MAX_BYTES = 4096

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
function jsonByteLength(value: unknown): number {
  const json = JSON.stringify(value)
  return typeof TextEncoder === 'function' ? new TextEncoder().encode(json).length : json.length
}
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}
/** A conforming map key: the character class first, then the dunder rejection. */
function isStorableKey(key: string, pattern: RegExp): boolean {
  if (!pattern.test(key)) return false
  if (RESERVED_KEY_RE.test(key)) return false
  return true
}

/**
 * The string rule. `slice` counts UTF-16 code units, so a cap that lands
 * between the two halves of an astral character (an emoji) would leave a lone
 * high surrogate at the end — not a character on its own, and a URL encoder
 * turns it into U+FFFD. Strip it, then trim again in case it followed a space.
 */
function capMetadataString(raw: string): string {
  const capped = raw.replace(/[\r\n]+/g, ' ').trim().slice(0, METADATA_VALUE_MAX)
  return capped.replace(/[\uD800-\uDBFF]$/, '').trim()
}

/** Free-form notes → the booking `message`: newlines kept (normalised), trimmed, capped. */
export function cappedNotes(value: unknown): string {
  return (typeof value === 'string' ? value : '').replace(/\r\n?/g, '\n').trim().slice(0, NOTES_MAX)
}

/** Pure: the custom-flow `metadata` map under the shared contract caps. */
export function sanitizeMetadata(input: unknown): BookingMetadata {
  if (!isPlainObject(input)) return {}
  const keys = Object.keys(input)
  if (keys.length > METADATA_MAX_KEYS) return {}
  const out: BookingMetadata = {}
  for (const key of keys) {
    if (!isStorableKey(key, METADATA_KEY_RE)) continue
    const value = input[key]
    if (typeof value === 'string') {
      const text = capMetadataString(value)
      if (text) out[key] = text
    }
    else if (isFiniteNumber(value) || typeof value === 'boolean') {
      out[key] = value
    }
  }
  return jsonByteLength(out) > METADATA_MAX_BYTES ? {} : out
}

/** Pure: intake answers keyed by question id (numbers are stringified, as a form field would). */
export function sanitizeIntake(input: unknown): BookingIntakeAnswers {
  if (!isPlainObject(input)) return {}
  const text = (value: string | number) => String(value).trim().slice(0, INTAKE_ANSWER_MAX)
  const out: BookingIntakeAnswers = {}
  for (const key of Object.keys(input)) {
    if (!isStorableKey(key, INTAKE_ID_RE)) continue
    const value = input[key]
    if (typeof value === 'string' || isFiniteNumber(value)) out[key] = text(value)
    else if (typeof value === 'boolean') out[key] = value
    else if (Array.isArray(value)) out[key] = value.filter((v): v is string | number => typeof v === 'string' || isFiniteNumber(v)).map(text)
  }
  return jsonByteLength(out) > INTAKE_MAX_BYTES ? {} : out
}
// Lead source (mount option `source`), mirrored rule for rule from the API's
// functions-schedules/src/workspace/schedules/publicBooking/bookingSource.ts
// (its header numbers the eight rules): `site` / `form` text ≤ 200;
// `ad_consent` granted | denied | unknown; `session_touch` text ≤ 200 with
// `landing_path` (path only: cut at ? or #) ≤ 500, `ts` finite ≥ 0 else 0, `gclid` / `fbclid` / `gbraid` / `wbraid` clean
// (^[A-Za-z0-9_.-]+$, ≤ 500, dropped never cut) and ONLY when granted;
// `first_touch` / `last_touch` (the `w369_attr` touch, plus a Meta-shaped
// `fbc`) ONLY when granted; an empty touch and an empty source are null; the
// serialised JSON must be ≤ 4096 bytes else null; own-property reads only.
export type BookingAdConsent = 'granted' | 'denied' | 'unknown'
export interface BookingSessionTouch {
  ts: number
  landing_path: string
  referrer_host: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
  utm_id: string
  gclid: string
  fbclid: string
  gbraid: string
  wbraid: string
}
export interface BookingTouch extends BookingSessionTouch {
  fbc: string
  w369_src: string
  w369_plc: string
}
export interface BookingSource {
  site: string
  form: string
  ad_consent: BookingAdConsent
  session_touch: BookingSessionTouch | null
  first_touch: BookingTouch | null
  last_touch: BookingTouch | null
}
const SOURCE_TEXT_MAX = 200
const SOURCE_PATH_MAX = 500
const SOURCE_CLICK_ID_MAX = 500
const SOURCE_MAX_BYTES = 4096
const SOURCE_SESSION_TEXT = ['referrer_host', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'] as const
const SOURCE_TOUCH_TEXT = [...SOURCE_SESSION_TEXT, 'w369_src', 'w369_plc'] as const
const SOURCE_CLICK_IDS = ['gclid', 'fbclid', 'gbraid', 'wbraid'] as const
const CLEAN_CLICK_ID_RE = /^[A-Za-z0-9_.-]+$/
const FB_COOKIE_RE = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_.-]+$/

function ownValue(obj: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined
}
function sourceText(value: unknown, max: number): string {
  if (typeof value !== 'string') return ''
  return value.replace(/[\r\n]+/g, ' ').trim().slice(0, max).replace(/[\uD800-\uDBFF]$/, '').trim()
}
function sourceClickId(value: unknown): string {
  return typeof value === 'string' && value.length <= SOURCE_CLICK_ID_MAX && CLEAN_CLICK_ID_RE.test(value) ? value : ''
}
// A path, never its query or fragment: a `?gclid=` there would dodge the consent gate.
function sourcePath(value: unknown): string {
  return sourceText(typeof value === 'string' ? value.split(/[?#]/)[0] : value, SOURCE_PATH_MAX)
}
function sourceTs(value: unknown): number {
  return isFiniteNumber(value) && value >= 0 ? value : 0
}
function isEmptyTouch(touch: object): boolean {
  return Object.values(touch).every(value => value === '' || value === 0)
}
function readSessionTouch(input: unknown, granted: boolean): BookingSessionTouch | null {
  if (!isPlainObject(input)) return null
  const touch = { ts: sourceTs(ownValue(input, 'ts')), landing_path: sourcePath(ownValue(input, 'landing_path')) } as BookingSessionTouch
  for (const field of SOURCE_SESSION_TEXT) touch[field] = sourceText(ownValue(input, field), SOURCE_TEXT_MAX)
  for (const field of SOURCE_CLICK_IDS) touch[field] = granted ? sourceClickId(ownValue(input, field)) : ''
  return isEmptyTouch(touch) ? null : touch
}
function readTouch(input: unknown): BookingTouch | null {
  if (!isPlainObject(input)) return null
  const touch = { ts: sourceTs(ownValue(input, 'ts')), landing_path: sourcePath(ownValue(input, 'landing_path')) } as BookingTouch
  for (const field of SOURCE_TOUCH_TEXT) touch[field] = sourceText(ownValue(input, field), SOURCE_TEXT_MAX)
  for (const field of SOURCE_CLICK_IDS) touch[field] = sourceClickId(ownValue(input, field))
  const fbc = ownValue(input, 'fbc')
  touch.fbc = typeof fbc === 'string' && fbc.length <= SOURCE_CLICK_ID_MAX && FB_COOKIE_RE.test(fbc) ? fbc : ''
  return isEmptyTouch(touch) ? null : touch
}

/** Pure: where the visit came from, under the shared contract; null when nothing is left. */
export function sanitizeSource(input: unknown): BookingSource | null {
  if (!isPlainObject(input)) return null
  const consent = ownValue(input, 'ad_consent')
  const adConsent: BookingAdConsent = consent === 'granted' || consent === 'denied' ? consent : 'unknown'
  const granted = adConsent === 'granted'
  const source: BookingSource = {
    site: sourceText(ownValue(input, 'site'), SOURCE_TEXT_MAX),
    form: sourceText(ownValue(input, 'form'), SOURCE_TEXT_MAX),
    ad_consent: adConsent,
    session_touch: readSessionTouch(ownValue(input, 'session_touch'), granted),
    first_touch: granted ? readTouch(ownValue(input, 'first_touch')) : null,
    last_touch: granted ? readTouch(ownValue(input, 'last_touch')) : null,
  }
  if (!source.site && !source.form && !source.session_touch && !source.first_touch && !source.last_touch) return null
  return jsonByteLength(source) > SOURCE_MAX_BYTES ? null : source
}
// ── END SHARED SANITISER ──────────────────────────────────────────────────
